import {QueryClient, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import IWBrokerClient from '../client/IWBrokerClient';
import {queryClient as defaultQueryClient} from './queryClient';

export const alarmStateQueryKey = ['00/alarm/state'] as const;
export const alarmDisplayQueryKey = ['00/alarm/display'] as const;
export const workshopDoorRollerStateQueryKey = ['00/workshop/door/roller'] as const;

export interface AlarmDisplay {
    line1: string;
    line2: string;
}

const installedBuildingStateListeners = new WeakSet<QueryClient>();
const liveStateQueryOptions = {
    staleTime: Infinity,
    gcTime: Infinity,
    networkMode: 'always' as const,
    retry: false
};

export function installBuildingStateQueryListeners(queryClient: QueryClient = defaultQueryClient) {
    if (installedBuildingStateListeners.has(queryClient)) {
        return;
    }

    queryClient.setQueryData(alarmStateQueryKey, '');
    queryClient.setQueryData<AlarmDisplay>(alarmDisplayQueryKey, {line1: '-', line2: '-'});
    queryClient.setQueryData(workshopDoorRollerStateQueryKey, '');

    IWBrokerClient.socket.on('00/alarm/state', (state: string) => {
        queryClient.setQueryData(alarmStateQueryKey, state);
    });
    IWBrokerClient.socket.on('00/alarm/display/line1', (line1: string) => {
        queryClient.setQueryData<AlarmDisplay>(alarmDisplayQueryKey, (current: AlarmDisplay | undefined) => ({
            line1,
            line2: current?.line2 ?? '-'
        }));
    });
    IWBrokerClient.socket.on('00/alarm/display/line2', (line2: string) => {
        queryClient.setQueryData<AlarmDisplay>(alarmDisplayQueryKey, (current: AlarmDisplay | undefined) => ({
            line1: current?.line1 ?? '-',
            line2
        }));
    });
    IWBrokerClient.socket.on('00/workshop/door/roller', (state: string) => {
        queryClient.setQueryData(workshopDoorRollerStateQueryKey, state);
    });

    installedBuildingStateListeners.add(queryClient);
}

export function useAlarmStateQuery(): UseQueryResult<string, Error> {
    const queryClient = useQueryClient();

    return useQuery<string>({
        queryKey: alarmStateQueryKey,
        queryFn: async (): Promise<string> => queryClient.getQueryData<string>(alarmStateQueryKey) ?? '',
        ...liveStateQueryOptions
    });
}

export function useAlarmDisplayQuery(): UseQueryResult<AlarmDisplay, Error> {
    const queryClient = useQueryClient();

    return useQuery<AlarmDisplay>({
        queryKey: alarmDisplayQueryKey,
        queryFn: async (): Promise<AlarmDisplay> => queryClient.getQueryData<AlarmDisplay>(alarmDisplayQueryKey)
            ?? {line1: '-', line2: '-'},
        ...liveStateQueryOptions
    });
}

export function useWorkshopDoorRollerStateQuery(): UseQueryResult<string, Error> {
    const queryClient = useQueryClient();

    return useQuery<string>({
        queryKey: workshopDoorRollerStateQueryKey,
        queryFn: async (): Promise<string> => queryClient.getQueryData<string>(workshopDoorRollerStateQueryKey) ?? '',
        ...liveStateQueryOptions
    });
}

export function useSetAlarmMutation() {
    return useMutation({
        mutationKey: ['setAlarm'],
        mutationFn: async (set: boolean) => {
            return IWBrokerClient.request(set ? '00/alarm/set' : '00/alarm/unset');
        }
    });
}

export function useSendAlarmKeyMutation() {
    return useMutation({
        mutationKey: ['00/alarm/key'],
        mutationFn: async (key: string) => IWBrokerClient.request('00/alarm/key', key)
    });
}

export function useWorkshopDoorRollerMutation() {
    return useMutation({
        mutationKey: ['00/workshop/door/roller'],
        mutationFn: async (state: string) => IWBrokerClient.volatileRequest('00/workshop/door/roller', state)
    });
}
